import { Link, useNavigate } from 'react-router-dom'
import { useState, useRef, useEffect, useCallback, useId } from 'react'
import { useSelectedOrg } from '@/context/SelectedOrgContext'
import { api } from '@/lib/api'
import { formatCount, formatDate, formatDateTime, relativeTime } from '@/lib/format'
import { Badge } from '@/shared/components/Badge'
import { ScanStatusBadge } from '@/shared/components/ScanStatusBadge'
import { TagIcon, DownloadIcon } from '@/shared/components/Icons'
import { styles } from '@/shell/layout.styles'
import { type SearchHit } from '@/shell/types'

/** "3d ago" for recent releases, a plain date once it's older than a month -
 *  relativeTime is uncapped and "400d ago" reads worse than the date. */
export function publishedAgo(ts: number): string {
  return Date.now() - ts > 30 * 86_400_000 ? formatDate(ts) : relativeTime(ts)
}

export function SearchBox() {
  const navigate = useNavigate()
  const { selectedOrg } = useSelectedOrg()
  const [value, setValue] = useState('')
  const [focused, setFocused] = useState(false)
  const [results, setResults] = useState<SearchHit[]>([])
  const [searched, setSearched] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeIndex, setActiveIndex] = useState(-1)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const blurRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()
  const optionId = (i: number) => `${listId}-opt-${i}`

  const reset = useCallback(() => {
    abortRef.current?.abort()
    setResults([])
    setSearched(false)
    setLoading(false)
    setError(null)
    setActiveIndex(-1)
  }, [])

  const search = useCallback(async (q: string) => {
    const term = q.trim()
    if (!term) { reset(); return }
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setLoading(true)
    try {
      const res = await api.get<{ modules: SearchHit[] }>(
        `/v1/modules/search?q=${encodeURIComponent(term)}&limit=8`,
        { signal: controller.signal },
      )
      setResults(res.modules ?? [])
      setError(null)
      setSearched(true)
      setActiveIndex(-1)
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return
      // Surface the failure instead of silently showing nothing.
      setResults([])
      setError(err instanceof Error && err.message ? err.message : 'Search failed')
      setSearched(true)
    } finally {
      // A superseded request must not clear the spinner of the one that replaced it.
      if (abortRef.current === controller) setLoading(false)
    }
  }, [reset])

  useEffect(() => () => {
    abortRef.current?.abort()
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (blurRef.current) clearTimeout(blurRef.current)
  }, [])

  // Keep the keyboard-highlighted option in view as ↑/↓ move through a scrolled list.
  useEffect(() => {
    if (activeIndex < 0) return
    document.getElementById(`${listId}-opt-${activeIndex}`)?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, listId])

  function close() {
    setValue('')
    reset()
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const q = e.target.value
    setValue(q)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => search(q), 300)
  }

  function handleSelect(hit: SearchHit) {
    close()
    inputRef.current?.blur()
    navigate(`/org/${hit.namespace}/modules/${hit.name}/${hit.provider}`)
  }

  // Results are kept so re-focusing the box brings them straight back.
  function handleBlur() {
    blurRef.current = setTimeout(() => { setFocused(false); setActiveIndex(-1) }, 150)
  }

  function handleFocus() {
    if (blurRef.current) clearTimeout(blurRef.current)
    setFocused(true)
  }

  const showDropdown = focused && value.trim().length > 0 && (results.length > 0 || searched || loading || error !== null)

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (results.length === 0) return
      e.preventDefault()
      const delta = e.key === 'ArrowDown' ? 1 : -1
      setActiveIndex((i) => {
        const from = i < 0 ? (delta > 0 ? -1 : 0) : i
        return (from + delta + results.length) % results.length
      })
    } else if (e.key === 'Enter') {
      const hit = results[activeIndex >= 0 ? activeIndex : 0]
      if (!hit) return
      e.preventDefault()
      handleSelect(hit)
    } else if (e.key === 'Escape') {
      // Stop here, or the window-level Escape handler also closes the mobile actions
      // menu on the same keypress. First Esc closes the results, a second one the menu.
      if (showDropdown) e.stopPropagation()
      close()
      e.currentTarget.blur()
    }
  }

  const headerText = error
    ? 'Search failed'
    : loading
      ? 'Searching…'
      : results.length > 0
        ? `${results.length} result${results.length === 1 ? '' : 's'}`
        : `No modules match “${value.trim()}”`

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showDropdown && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        style={{ ...styles.searchBox, width: focused || value ? 260 : 140 }}
        placeholder="Search modules…"
        aria-label="Search modules"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      {showDropdown && (
        // mousedown is swallowed so clicking an option, the footer link or the list's
        // scrollbar never blurs the input (which would close the panel mid-click).
        <div style={styles.searchDropdown} onMouseDown={(e) => e.preventDefault()}>
          <div style={styles.searchHeader}>
            <span>{headerText}</span>
            <span style={styles.searchKbdHint} aria-hidden="true">↑↓ · ↵ open · esc</span>
          </div>
          {error && <div style={styles.searchError}>{error}</div>}
          {!error && !loading && searched && results.length === 0 && (
            <div style={styles.searchEmpty}>Try a module name or a word from its description.</div>
          )}
          <div role="listbox" id={listId} aria-label="Module results" style={styles.searchList}>
            {results.map((hit, i) => {
              const active = i === activeIndex
              const last = i === results.length - 1 && !!selectedOrg
              const publishedTs = Date.parse(hit.published_at)
              const hasDate = !Number.isNaN(publishedTs)
              return (
                <div
                  key={hit.id}
                  id={optionId(i)}
                  role="option"
                  aria-selected={active}
                  tabIndex={-1}
                  style={{
                    ...styles.searchOption,
                    ...(active ? styles.searchOptionActive : {}),
                    ...(last ? { borderBottom: 'none' } : {}),
                  }}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => handleSelect(hit)}
                >
                  <div style={styles.searchOptionTop}>
                    <span style={styles.searchOptionName}>
                      <span style={styles.searchOptionNs}>{hit.namespace}/</span>{hit.name}
                    </span>
                    <span style={styles.searchOptionVersion}>v{hit.version}</span>
                  </div>
                  {hit.description
                    ? <div style={styles.searchOptionDesc}>{hit.description}</div>
                    : <div style={styles.searchOptionDescEmpty}>No description</div>}
                  <div style={styles.searchStats}>
                    <Badge variant="accent">{hit.provider}</Badge>
                    <span style={styles.searchStat} title="Downloads through the registry">
                      <DownloadIcon size={11} />{formatCount(hit.downloads)}
                    </span>
                    <span style={styles.searchStat} title="Published versions">
                      <TagIcon size={11} />{formatCount(hit.version_count)}
                    </span>
                    <span style={styles.searchStat} {...(hasDate ? { title: formatDateTime(publishedTs) } : {})}>
                      published {hasDate ? publishedAgo(publishedTs) : '-'}
                    </span>
                    <ScanStatusBadge
                      status={hit.scan_status}
                      critical={hit.critical_count}
                      high={hit.high_count}
                      medium={hit.medium_count}
                      low={hit.low_count}
                    />
                    {hit.deprecated && (
                      <Badge variant="amber" className="badge-still" {...(hit.deprecated_message ? { title: hit.deprecated_message } : {})}>
                        deprecated
                      </Badge>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          {selectedOrg && (
            <Link
              to={`/org/${selectedOrg.slug}/modules`}
              style={styles.searchFooterLink}
              onClick={() => { close(); inputRef.current?.blur() }}
            >
              Browse all modules in {selectedOrg.slug} →
            </Link>
          )}
        </div>
      )}
    </div>
  )
}

